import {
	consumeTapGesture,
	decideImmersiveTap,
	type GesturePointerSample,
	IDLE_TAP_GESTURE,
	type TapGesture,
	type TapPoint,
	tapGestureDown,
	tapGestureMove,
	tapGestureUp,
} from '@polygraf/shared'
import {
	type MouseEvent as ReactMouseEvent,
	type PointerEvent as ReactPointerEvent,
	useCallback,
	useEffect,
	useRef,
} from 'react'

/** Taps here have their own job and never count towards the double tap. */
const CONTROL_SELECTOR = 'a, button, input, label, select, textarea, [role="dialog"]'

function sample(e: PointerEvent | ReactPointerEvent): GesturePointerSample {
	return {
		pointerId: e.pointerId,
		pointerType: e.pointerType,
		isPrimary: e.isPrimary,
		x: e.clientX,
		y: e.clientY,
	}
}

/**
 * Double tap in the viewer's content LEAVES immersive (full-screen) mode. It
 * never enters it: entering is the header button's (or F's) job, so a double
 * tap cannot collide with pan and zoom — agreed with MU on 25. 9. 2026 for
 * the transcript and image viewers. The rules live in `decideImmersiveTap`
 * (@polygraf/shared); this hook only feeds it pointer events.
 *
 * Spread the returned handlers on the content element. Pointer moves, lifts
 * and cancels are watched on the window, so a finger or mouse released
 * outside the element still ends its press.
 *
 * VoiceOver: its double tap is ONE click to the page, so it never exits
 * from here. VoiceOver users have the labelled header button.
 */
export function useImmersiveDoubleTap(immersive: boolean, onExit: () => void) {
	const gestureRef = useRef<TapGesture>(IDLE_TAP_GESTURE)
	const pendingTapRef = useRef<TapPoint | undefined>(undefined)

	useEffect(() => {
		const move = (e: PointerEvent) => {
			gestureRef.current = tapGestureMove(gestureRef.current, sample(e))
		}
		const up = (e: PointerEvent) => {
			gestureRef.current = tapGestureUp(gestureRef.current, e.pointerId, false)
		}
		// The browser took the pointer for a scroll or its own pinch zoom.
		const cancel = (e: PointerEvent) => {
			gestureRef.current = tapGestureUp(gestureRef.current, e.pointerId, true)
		}
		// Capture phase: a handler further down that stops propagation must not
		// hide a lift from us, or the pointer would stay "down" and the next
		// tap would read as a second finger.
		window.addEventListener('pointermove', move, true)
		window.addEventListener('pointerup', up, true)
		window.addEventListener('pointercancel', cancel, true)
		return () => {
			window.removeEventListener('pointermove', move, true)
			window.removeEventListener('pointerup', up, true)
			window.removeEventListener('pointercancel', cancel, true)
		}
	}, [])

	const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
		gestureRef.current = tapGestureDown(gestureRef.current, sample(e))
	}, [])

	const onClick = useCallback(
		(e: ReactMouseEvent<HTMLElement>) => {
			const gesture = gestureRef.current
			// Consume the press: a click with no pointerdown before it (a
			// synthetic activation) must fall back to the device's primary pointer.
			gestureRef.current = consumeTapGesture(gesture)
			const target = e.target as HTMLElement | null
			const decision = decideImmersiveTap({
				immersive,
				downPointerType: gesture.downPointerType,
				anyCoarsePointer: !!globalThis.matchMedia?.('(any-pointer: coarse)').matches,
				ignored: gesture.disqualified || !!target?.closest(CONTROL_SELECTOR),
				hasSelection: (globalThis.getSelection?.()?.toString().length ?? 0) > 0,
				tap: { time: e.timeStamp, x: e.clientX, y: e.clientY },
				pendingTap: pendingTapRef.current,
			})
			pendingTapRef.current = decision.pendingTap
			if (decision.exit) onExit()
		},
		[immersive, onExit],
	)

	return { onPointerDown, onClick }
}
